import { appStorage } from './betaStorage'
import { useEffect, useState } from 'react'
import { profilePhotoFor } from './profilePhotos'

export type YouthParticipantId = 'u-leo' | 'u-jnr'

export type NixiTrackingSummary = {
  regulationCheckIns: number
  breakRequests: number
  routineProgress: number
  communicationChoices: number
  summary: string
}

export type YouthParticipantProfile = {
  id: YouthParticipantId
  parentUserId: 'u-lillian'
  name: string
  age: number
  parentName: string
  relationLabel: string
  photoUrl: string
  nextAppointment: { day: string; time: string; title: string; detail: string }
  serviceBudget: { allocated: string; spent: string; remaining: string }
  goals: Array<{ title: string; progress: number; everyday: string }>
  latestUpdate: { from: string; text: string }
  nixiTracking: NixiTrackingSummary
}

const makeProfile = (
  id: YouthParticipantId,
  details: Omit<YouthParticipantProfile, 'id' | 'photoUrl'>,
): YouthParticipantProfile => ({
  id,
  ...details,
  get photoUrl() {
    return profilePhotoFor(id) ?? ''
  },
})

export const youthParticipantProfiles: YouthParticipantProfile[] = [
  makeProfile('u-leo', {
    parentUserId: 'u-lillian',
    name: 'Leo',
    age: 8,
    parentName: 'Lillian Carter',
    relationLabel: 'Mum · Plan Nominee / Admin',
    nextAppointment: {
      day: 'Thursday',
      time: '4:00 pm',
      title: 'Speech pathology',
      detail: 'Functional communication and Tell Someone choices.',
    },
    serviceBudget: { allocated: '$3,600', spent: '$1,050', remaining: '$2,550' },
    goals: [
      { title: 'Ask for a break before becoming overwhelmed', progress: 65, everyday: 'Tell Someone + regulation buttons in Nixi' },
      { title: 'Complete morning routine with fewer verbal prompts', progress: 72, everyday: 'Nixi morning visual routine' },
    ],
    latestUpdate: {
      from: 'Casey Morgan',
      text: 'Leo used the quiet-space option after school without needing a verbal prompt.',
    },
    nixiTracking: {
      regulationCheckIns: 18,
      breakRequests: 9,
      routineProgress: 72,
      communicationChoices: 24,
      summary: 'Steady regulation check-ins and growing independence across morning routines.',
    },
  }),
  makeProfile('u-jnr', {
    parentUserId: 'u-lillian',
    name: 'Lizzie',
    age: 4,
    parentName: 'Lillian Carter',
    relationLabel: 'Mum · Plan Nominee / Admin',
    nextAppointment: {
      day: 'Tuesday',
      time: '9:30 am',
      title: 'Early childhood OT',
      detail: 'Play-based regulation, transitions and two-step routines.',
    },
    serviceBudget: { allocated: '$3,200', spent: '$880', remaining: '$2,320' },
    goals: [
      { title: 'Choose help or a break using Nixi', progress: 58, everyday: 'Large visual choices with spoken prompts' },
      { title: 'Complete a two-step play routine', progress: 64, everyday: 'First–then Nixi routine cards' },
    ],
    latestUpdate: {
      from: 'Lillian Carter',
      text: 'Lizzie used the purple Nixi break button twice this week and returned to play calmly.',
    },
    nixiTracking: {
      regulationCheckIns: 14,
      breakRequests: 6,
      routineProgress: 64,
      communicationChoices: 21,
      summary: 'Break requests are increasing and two-step play routines are becoming more consistent.',
    },
  }),
]

const STORAGE_KEY = 'marnixia.selectedYouthParticipant.v1'
const CHANGE_EVENT = 'marnixia:youth-participant-change'

export function youthParticipantById(id: string | null | undefined) {
  return youthParticipantProfiles.find((profile) => profile.id === id) ?? youthParticipantProfiles[0]
}

export function youthParticipantsForParent(parentUserId: string) {
  return youthParticipantProfiles.filter(profile => profile.parentUserId === parentUserId)
}

export function getSelectedYouthParticipant() {
  try {
    return youthParticipantById(appStorage.getItem(STORAGE_KEY))
  } catch {
    return youthParticipantProfiles[0]
  }
}

export function setSelectedYouthParticipant(id: YouthParticipantId) {
  try {
    appStorage.setItem(STORAGE_KEY, id)
  } catch {
    // Keep the live prototype usable if storage is unavailable.
  }
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { id } }))
}

export function useSelectedYouthParticipant() {
  const [participant, setParticipant] = useState(getSelectedYouthParticipant)

  useEffect(() => {
    const refresh = () => setParticipant(getSelectedYouthParticipant())
    window.addEventListener(CHANGE_EVENT, refresh)
    window.addEventListener('storage', refresh)
    return () => {
      window.removeEventListener(CHANGE_EVENT, refresh)
      window.removeEventListener('storage', refresh)
    }
  }, [])

  return {
    participant,
    selectParticipant: setSelectedYouthParticipant,
  }
}

